import { z } from 'zod';
/**
 * Self-serve billing (docs/adr/0044-billing-and-trials.md): trials, Stripe subscriptions per seat, and the
 * read-only state a workspace enters when it is not paid for. Billing never deletes data.
 *
 * Prices are not in the code: each plan's Stripe Price ids live in the platform admin configuration
 * (PlatformSettings.billing), so amounts change in Stripe without a release.
 */
/**
 * Solo, Team and Business are self-serve through Stripe Checkout; Enterprise is "contact us". Solo (Freelancer) is
 * one seat: the owner (docs/adr/0060-solo-plan-and-own-workspaces.md).
 */
export const BillingPlanKey = z.enum(['solo', 'team', 'business', 'enterprise']);
export const SelfServePlanKey = z.enum(['solo', 'team', 'business']);
export const BillingInterval = z.enum(['month', 'year']);
export const BILLING_PLANS = {
    solo: { label: 'Solo', summary: 'For a freelancer running their own clients.', points: ['One person: you', 'Clients, time tracking, invoicing and the client portal', 'Guests and client portal users are free'] },
    team: { label: 'Team', summary: 'Everything a small team needs to run its clients.', points: ['Every feature', 'Guests and client portal users are free', 'Email support'] },
    business: { label: 'Business', summary: 'For teams with stricter security needs.', points: ['Everything in Team', 'Two-factor sign-in required for everyone', 'Single sign-on (SAML or OpenID Connect) and SCIM'] },
    enterprise: { label: 'Enterprise', summary: 'For larger organisations with their own terms.', points: ['Everything in Business', 'Dedicated database option', 'Custom DPA and invoicing'] },
};
/**
 * Joined seats a plan allows (docs/adr/0060): Solo is the owner alone. Other plans have no limit. Guests, client
 * portal contacts and invited people who haven't joined are never seats, so they are never limited here; but on
 * Solo nobody else can be invited as an owner, admin or member, because they would become a second seat.
 */
export const PLAN_SEAT_LIMITS = { solo: 1 };
/** The seat limit of a plan key (from `tenants.plan` or the subscription), or null when there is none. */
export function seatLimitOf(plan) {
    const key = BillingPlanKey.safeParse(plan).data;
    return key ? (PLAN_SEAT_LIMITS[key] ?? null) : null;
}
/** What the server says when a Solo workspace tries to add a second person (403 `plan_required`). */
export const SOLO_SEAT_MESSAGE = 'Solo is for one person. Upgrade to Team to invite teammates; guests and clients stay free.';
/**
 * Where a workspace stands:
 *  - trialing: inside its free trial (no card needed), or a subscription still in Stripe's trial
 *  - active: paid
 *  - past_due: a payment failed and the grace period is running; everything still works
 *  - read_only: the trial ended unpaid, the grace period ended, or the subscription was cancelled.
 *    People can sign in, read, search and export; nobody can post or create. Nothing is deleted.
 *  - comped: complimentary, set by platform staff (Pixelscope); never needs billing
 */
export const BillingState = z.enum(['trialing', 'active', 'past_due', 'read_only', 'comped']);
export const ReadOnlyReason = z.enum(['trial_ended', 'payment_failed', 'cancelled']);
/** Stripe subscription statuses we store. */
export const SubscriptionStatus = z.enum(['incomplete', 'incomplete_expired', 'trialing', 'active', 'past_due', 'canceled', 'unpaid', 'paused']);
export const DEFAULT_GRACE_DAYS = 7;
const DAY_MS = 86_400_000;
/** Days left until `end`, rounded up (23 hours left is 1 day). Never negative. */
export function daysLeft(end, now) {
    return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / DAY_MS));
}
/** The single rule for a workspace's billing state, shared by the API guard, the worker and the platform admin. */
export function billingStanding(f, now) {
    const none = { trialDaysLeft: null, graceEndsAt: null };
    if (f.comped)
        return { state: 'comped', readOnlyReason: null, ...none };
    const trialRunning = !f.trialEndsAt || f.trialEndsAt.getTime() > now.getTime();
    const trialDaysLeft = f.trialEndsAt && trialRunning ? daysLeft(f.trialEndsAt, now) : null;
    const status = f.subscriptionStatus;
    const failing = status === 'past_due' || status === 'unpaid' || (f.pastDueSince !== null && (status === 'active' || status === 'trialing'));
    if (failing) {
        const since = f.pastDueSince ?? now;
        const graceEndsAt = new Date(since.getTime() + f.graceDays * DAY_MS);
        if (graceEndsAt.getTime() > now.getTime())
            return { state: 'past_due', readOnlyReason: null, trialDaysLeft: null, graceEndsAt };
        return { state: 'read_only', readOnlyReason: 'payment_failed', trialDaysLeft: null, graceEndsAt };
    }
    if (status === 'active')
        return { state: 'active', readOnlyReason: null, ...none };
    if (status === 'trialing')
        return { state: 'trialing', readOnlyReason: null, trialDaysLeft, graceEndsAt: null };
    // No usable subscription: the workspace's own trial decides. A cancelled one is read-only once the trial is over.
    if (trialRunning)
        return { state: 'trialing', readOnlyReason: null, trialDaysLeft, graceEndsAt: null };
    const cancelled = status === 'canceled' || status === 'incomplete_expired' || status === 'paused';
    return { state: 'read_only', readOnlyReason: cancelled ? 'cancelled' : 'trial_ended', ...none };
}
/** Trial reminder emails: 7, 3 and 1 day before the end, and on the day it ends. */
export const TrialNotice = z.enum(['7', '3', '1', 'expired']);
const NOTICE_DAYS = [
    { notice: '1', days: 1 },
    { notice: '3', days: 3 },
    { notice: '7', days: 7 },
];
/**
 * The trial email due now, if any, and every notice it settles. Only the tightest one reached is sent: a
 * workspace first seen with 2 days left gets the 3-day email, never the 7-day one as well.
 */
export function dueTrialNotice(trialEndsAt, now, sent) {
    const msLeft = trialEndsAt.getTime() - now.getTime();
    if (msLeft <= 0)
        return sent.includes('expired') ? null : { notice: 'expired', settles: ['7', '3', '1', 'expired'] };
    const reached = NOTICE_DAYS.find((n) => msLeft <= n.days * DAY_MS);
    if (!reached || sent.includes(reached.notice))
        return null;
    const settles = NOTICE_DAYS.filter((n) => n.days >= reached.days).map((n) => n.notice);
    return { notice: reached.notice, settles };
}
/** Monthly recurring revenue of one subscription, in cents, before discounts and tax. */
export function monthlyRecurringCents(s) {
    if ((s.state !== 'active' && s.state !== 'past_due') || !s.amountCents || !s.seats || !s.interval)
        return 0;
    const period = s.amountCents * s.seats;
    return Math.round(s.interval === 'year' ? period / 12 : period);
}
/**
 * An Australian Business Number: 11 digits whose weighted sum (first digit less one) is divisible by 89.
 * Spaces are ignored. Returns the 11 digits, or null if it is not a valid ABN.
 */
export function normaliseAbn(input) {
    const digits = input.replace(/\s+/g, '');
    if (!/^\d{11}$/.test(digits))
        return null;
    const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
    const sum = digits.split('').reduce((n, d, i) => n + (Number(d) - (i === 0 ? 1 : 0)) * weights[i], 0);
    return sum % 89 === 0 ? digits : null;
}
export const Abn = z.string().trim().max(20).transform((v, ctx) => {
    const abn = normaliseAbn(v);
    if (!abn) {
        ctx.addIssue({ code: 'custom', message: 'Enter a valid 11-digit ABN' });
        return z.NEVER;
    }
    return abn;
});
// --- API shapes -----------------------------------------------------------------------------------
/** What everyone in the workspace may know: drives the trial and read-only banners. */
export const BillingStatus = z.object({
    state: BillingState,
    readOnlyReason: ReadOnlyReason.nullable(),
    trialEndsAt: z.string().nullable(),
    trialDaysLeft: z.number().int().nullable(),
    graceEndsAt: z.string().nullable(),
    plan: BillingPlanKey.nullable(),
    /** Whether this person may open the billing page (the owner, or an admin the owner granted billing to). */
    canManage: z.boolean(),
    /** Whether this server can take payments (Stripe is configured). */
    available: z.boolean(),
});
export const BillingPlanOption = z.object({
    key: BillingPlanKey,
    label: z.string(),
    summary: z.string(),
    points: z.array(z.string()),
    /** "Contact us" (Enterprise). */
    contact: z.boolean(),
    /** Joined seats the plan allows (Solo: 1), or null for no limit. */
    seatLimit: z.number().int().nullable(),
    /** Per seat, per interval, from the Stripe Price; null when that interval isn't configured. */
    prices: z.object({
        month: z.object({ amountCents: z.number().int().nullable(), currency: z.string() }).nullable(),
        year: z.object({ amountCents: z.number().int().nullable(), currency: z.string() }).nullable(),
    }),
});
export const BillingOverview = BillingStatus.extend({
    interval: BillingInterval.nullable(),
    subscriptionStatus: SubscriptionStatus.nullable(),
    cancelAtPeriodEnd: z.boolean(),
    /** Active members now: owners, admins and members. Guests and client portal users are free. */
    seats: z.number().int(),
    /**
     * Owners, admins and members, joined or invited (guests excluded). Solo can be chosen only while this is 1
     * (docs/adr/0060).
     */
    teamMembers: z.number().int(),
    /** The quantity on the subscription (synced from member changes). */
    billedSeats: z.number().int().nullable(),
    nextInvoice: z.object({ date: z.string(), amountCents: z.number().int(), currency: z.string() }).nullable(),
    billingEmail: z.string().nullable(),
    abn: z.string().nullable(),
    /** Admins the owner allowed to manage billing (user ids). */
    billingAdmins: z.array(z.uuid()),
    /** Only owners change who else may manage billing. */
    canGrant: z.boolean(),
    /** GST through Stripe Tax (platform setting). */
    stripeTax: z.boolean(),
    plans: z.array(BillingPlanOption),
    /** A Checkout already open for this workspace: choosing the same plan continues it until it expires. */
    openCheckout: z.object({ plan: SelfServePlanKey, interval: BillingInterval, expiresAt: z.string() }).nullable(),
});
export const StartCheckoutInput = z.object({ plan: SelfServePlanKey, interval: BillingInterval });
export const RedirectResponse = z.object({ url: z.url() });
/**
 * Where "Choose plan" sends the owner (docs/adr/0044): a Stripe Checkout (new, or the one already open for this
 * workspace when `reused`), or the Customer Portal when a subscription is already live (plans change there).
 */
export const CheckoutResponse = RedirectResponse.extend({ kind: z.enum(['checkout', 'portal']), reused: z.boolean() });
export const UpdateBillingDetailsInput = z.object({
    billingEmail: z.email().max(254).nullable().optional(),
    abn: Abn.nullable().optional(),
    /** Owners only: the admins who may manage billing. */
    billingAdmins: z.array(z.uuid()).max(20).optional(),
});
// --- Onboarding checklist (My work, docs/adr/0044) ------------------------------------------------
export const OnboardingItemKey = z.enum(['invite_team', 'client_channel', 'support_inbox', 'import', 'desktop_app', 'phone_app', 'choose_plan', 'log_time', 'first_invoice']);
export const OnboardingChecklist = z.object({
    /** Shown to owners and admins until they dismiss it or everything is done. */
    show: z.boolean(),
    /**
     * The workspace's first week is over: My work shows the checklist as one progress line, opened on request
     * (v4 RESTYLE_BRIEF.md finding 10). Missing from an older API.
     */
    collapsed: z.boolean().optional(),
    /**
     * `solo`: a workspace for one person (made with the Freelancer preset, or on the Solo plan; docs/adr/0060). Its
     * checklist has no team steps: no "invite your team", and log time and send an invoice instead.
     */
    kind: z.enum(['team', 'solo']),
    items: z.array(z.object({ key: OnboardingItemKey, done: z.boolean() })),
});
/**
 * The one sentence every disabled write control shows while the workspace is read-only (docs/adr/0044), on
 * the web, the phone and the client portal.
 */
export const READ_ONLY_EXPLANATION = "This workspace is read-only right now, so this can't be changed. Reading, searching and exporting still work.";
/** Whether the workspace is read-only for billing, from the status everyone can read (web and phone). */
export function isWorkspaceReadOnly(status) {
    return !!status && status.available && status.state === 'read_only';
}
//# sourceMappingURL=billing.js.map